import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {
  Button,
  DropdownItem,
  SortableContainerContext,
  Text,
  ListGroupItem,
  Icon
} from '@albert-io/atomic';
import {
  getCurrentSort,
  clearSortValue
} from 'components/PracticeView/PracticeViewToolbar/Sort/sort.utils';

import Sort from 'components/PracticeView/PracticeViewToolbar/Sort/Sort.react';
import PracticeViewToolbar from 'components/PracticeView/PracticeViewToolbar/PracticeViewToolbar.react';
import SegmentedToolbarButton from 'components/PracticeView/PracticeViewToolbar/SegmentedToolbarButton/SegmentedToolbarButton.react';

const GuideLevelToolbar = ({
  location,
  selectedQuestionCount,
  questionCount,
  clearQuestionSetSelections,
  cancelRearranging,
  setManualSort,
  saveReordering,
  isSavingReorder,
  hasSavedReorder,
  isSelectAllQuestionSetsMode,
  getSelectedSetIndices,
  isManualSort,
  duplicateQuestionSetIds,
  isEmptyPracticeView
}) => {
  const hasSelectedQuestions = !!selectedQuestionCount;
  const urlQueries = location.query;
  const isSorting = !!urlQueries?.sort;
  let savingText;
  if (isSavingReorder) {
    savingText = (
      <Text size='s' className='u-display_flex u-align-items_center'>
        <Icon className='u-mar-r_1' icon='spinner' pulse />
        Saving...
      </Text>
    );
  } else if (hasSavedReorder) {
    savingText = (
      <Text size='s'>
        <Icon className='u-mar-r_1' icon={['far', 'check']} />
        All changes saved
      </Text>
    );
  }
  if (isEmptyPracticeView) {
    return <PracticeViewToolbar />;
  }
  if (!duplicateQuestionSetIds.isEmpty()) {
    return (
      <PracticeViewToolbar>
        <Text size='m' color='red' className='u-mar-tb_auto u-mar-l_1 u-font-weight_bold'>
          <Icon className='u-mar-r_1' icon={['far', 'exclamation-triangle']} />
          {duplicateQuestionSetIds.size} question(s) (published or otherwise) have duplicate
          alignment. You must re-align each question to a single level before reordering.
        </Text>
      </PracticeViewToolbar>
    );
  }
  if (isManualSort) {
    return (
      <SortableContainerContext.Consumer>
        {(value) => {
          if (!value) {
            return null;
          }
          const {setHasSorted, cancelSortMode} = value;
          return (
            <PracticeViewToolbar>
              <Button
                size='s'
                color='secondary'
                variant='text'
                onClick={() => cancelRearranging({cancelSortMode, setHasSorted})}
                className='u-mar-r_1'
                border
              >
                <Icon icon='times' />
              </Button>
              <Text size='m'>Click between questions to move a question.</Text>
            </PracticeViewToolbar>
          );
        }}
      </SortableContainerContext.Consumer>
    );
  }
  if (hasSelectedQuestions) {
    const selectedIndices = getSelectedSetIndices();
    const allSetsAreSelected =
      (selectedIndices.isEmpty() && isSelectAllQuestionSetsMode) ||
      questionCount === selectedQuestionCount;
    return (
      <SortableContainerContext.Consumer>
        {(value) => {
          if (!value) {
            return null;
          }
          const {handleMultiSortStart, handleMoveToTop, setHasSorted} = value;

          return (
            <PracticeViewToolbar>
              <Button size='s' onClick={clearQuestionSetSelections} className='u-mar-r_1'>
                Clear selection
              </Button>

              <SegmentedToolbarButton className='u-mar-r_1' backgroundColor='secondary'>
                <SegmentedToolbarButton.Button
                  disabled={isSelectAllQuestionSetsMode || allSetsAreSelected}
                  backgroundColor='secondary'
                  onClick={() => {
                    handleMultiSortStart(selectedIndices);
                    setManualSort(true);
                  }}
                  text='Move questions'
                />
                <SegmentedToolbarButton.Dropdown disabled={allSetsAreSelected}>
                  <DropdownItem
                    as='button'
                    onClick={(e) => {
                      // With how we handle SelectAll, we do the inverse action since we save the ID's of the questions that are deselected
                      if (!isSelectAllQuestionSetsMode) {
                        handleMoveToTop(e, selectedIndices);
                      } else {
                        saveReordering(setHasSorted, true);
                      }
                    }}
                  >
                    Move to top
                  </DropdownItem>
                  <DropdownItem
                    as='button'
                    onClick={(e) => {
                      if (!isSelectAllQuestionSetsMode) {
                        saveReordering(setHasSorted, true);
                      } else {
                        handleMoveToTop(e, selectedIndices);
                      }
                    }}
                  >
                    Move to bottom
                  </DropdownItem>
                </SegmentedToolbarButton.Dropdown>
              </SegmentedToolbarButton>
              {savingText && (
                <Text size='m' className='u-mar-tb_auto u-mar-l_1'>
                  {savingText}
                </Text>
              )}
            </PracticeViewToolbar>
          );
        }}
      </SortableContainerContext.Consumer>
    );
  }
  return (
    <PracticeViewToolbar>
      <Sort
        active={isSorting}
        ignoredActions={['random', 'difficulty']}
        triggerButtonText='Order by'
        icon='caret-down'
        iconPosition='right'
        additionalDropdownItems={
          <>
            <ListGroupItem as={DropdownItem} onClick={clearSortValue}>
              <Text
                bold
                emphasis={getCurrentSort() === undefined ? 'high' : 'medium'}
                color={getCurrentSort() === undefined ? 'blue' : 'black'}
              >
                Custom order
              </Text>
            </ListGroupItem>
          </>
        }
      />
      {savingText && (
        <Text size='m' className='u-mar-tb_auto u-mar-l_1'>
          {savingText}
        </Text>
      )}
    </PracticeViewToolbar>
  );
};

GuideLevelToolbar.propTypes = {
  location: PropTypes.object,
  selectedQuestionCount: PropTypes.number,
  questionCount: PropTypes.number,
  clearQuestionSetSelections: PropTypes.func,
  cancelRearranging: PropTypes.func,
  saveReordering: PropTypes.func,
  getSelectedSetIndices: PropTypes.func,
  isSavingReorder: PropTypes.bool,
  hasSavedReorder: PropTypes.bool,
  isSelectAllQuestionSetsMode: PropTypes.bool,
  setManualSort: PropTypes.func,
  isManualSort: PropTypes.bool,
  duplicateQuestionSetIds: ImmutablePropTypes.list,
  isEmptyPracticeView: PropTypes.bool
};

export default GuideLevelToolbar;
